import { resolveFilestackApiKey } from "./filestackApiKey";
import { retryWhileDocumentHidden } from "./whenDocumentVisible";

export const FILESTACK_UPLOAD_OPTIONS = {
  retry: 5,
  timeout: 180000,
};

export const AI_CHAT_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

export function isImageFile(file) {
  return Boolean(file && AI_CHAT_IMAGE_MIME_TYPES.includes(file.type));
}

export function imageFilesFromDataTransfer(dataTransfer) {
  return Array.from(dataTransfer?.files || []).filter(isImageFile);
}

export function dataTransferHasFiles(dataTransfer) {
  if (!dataTransfer) return false;

  const items = Array.from(dataTransfer.items || []);
  if (items.length > 0) {
    return items.some((item) => item.kind === "file");
  }

  if (Array.from(dataTransfer.types || []).includes("Files")) {
    return true;
  }

  return Array.from(dataTransfer.files || []).length > 0;
}

export function dataTransferHasImages(dataTransfer) {
  if (!dataTransfer) return false;

  const items = Array.from(dataTransfer.items || []);
  if (items.length > 0) {
    return items.some(
      (item) =>
        item.kind === "file" && AI_CHAT_IMAGE_MIME_TYPES.includes(item.type)
    );
  }

  return imageFilesFromDataTransfer(dataTransfer).length > 0;
}

export function sanitizeUploadFilename(filename) {
  return String(filename || "")
    .replace(/ /g, "_")
    .replace(/\(/g, "-")
    .replace(/\)/g, "-")
    .replace(/\+/g, "-");
}

export const MAX_IMAGE_UPLOAD_BYTES = 1024 * 1024 * 10;

export function takeImageFiles(files, limit) {
  const all = Array.from(files || []);
  const images = all.filter(isImageFile);
  const sized = images.filter(
    (file) => Number(file.size) <= MAX_IMAGE_UPLOAD_BYTES
  );
  const max = Number.isFinite(limit) ? Math.max(0, limit) : sized.length;
  return {
    accepted: sized.slice(0, max),
    omitted: Math.max(0, sized.length - max),
    oversized: images.length - sized.length,
    unsupported: all.length - images.length,
  };
}

export const UPLOAD_WAIT_MESSAGE = "Wait for the image upload to finish.";

export function imageDropLimitMessage({
  omitted = 0,
  oversized = 0,
  unsupported = 0,
  limitLabel,
}) {
  const parts = [];
  if (unsupported > 0) {
    parts.push("Only JPG, PNG, GIF or WebP images can be attached.");
  }
  if (oversized > 0) parts.push("Images must be 10MB or smaller.");
  if (omitted > 0) parts.push(`You can attach up to ${limitLabel}.`);
  return parts.join(" ");
}

export function uploadFailureMessage(error) {
  return error && error.message === "nsfw"
    ? "That image does not meet our content policies."
    : "Unable to attach that image. Try the + button.";
}

export function uploadLocalImageFile({
  file,
  environment,
  path,
  websiteId,
  apiKey,
  forceNsfwCheck = false,
  skipProcessImage = false,
  library = false,
  onUploadStarted,
  callback,
}) {
  const uploads = typeof window !== "undefined" ? window.uploads : null;
  const filestack = typeof window !== "undefined" ? window.filestack : null;
  if (
    !file ||
    !uploads ||
    typeof uploads.transform !== "function" ||
    !filestack ||
    typeof filestack.init !== "function"
  ) {
    return Promise.reject(new Error("image upload is unavailable"));
  }

  const storePath = `/${environment}${path}`;
  const filename = sanitizeUploadFilename(file.name);
  const uploadFile =
    filename === file.name
      ? file
      : new File([file], filename, {
          type: file.type,
          lastModified: file.lastModified,
        });
  if (typeof onUploadStarted === "function") onUploadStarted(uploadFile);

  return new Promise((resolve, reject) => {
    retryWhileDocumentHidden(() =>
      filestack
        .init(resolveFilestackApiKey(apiKey))
        .upload(uploadFile, FILESTACK_UPLOAD_OPTIONS, {
          location: "s3",
          path: storePath,
          access: "public",
        })
    )
      .then((uploaded) => {
        uploads.transform(
          "callback",
          {
            environment,
            path,
            website_id: websiteId,
            forceNsfwCheck,
            skipProcessImage,
            library,
            callback: (...args) => {
              if (typeof callback === "function") callback(...args);
              resolve(args[0]);
            },
            onUploadFailed: (reason) =>
              reject(new Error(reason || "upload failed")),
          },
          {
            ...uploaded,
            filename,
            mimetype: file.type,
            source: "local_file_system",
          }
        );
      })
      .catch(reject);
  });
}
